/**
 * Firebase Analytics (GA4) — thin, safe wrapper.
 *
 * Analytics initializes lazily and stays fully disabled unless ALL of:
 *   1. it was explicitly enabled via enableAnalytics() — only the customer app
 *      entry (src/main.tsx) does this, so the internal QA app (src/qa/main.tsx),
 *      which shares this module through useAuth, never reports. Per the decision
 *      to keep customer product metrics free of internal-tool usage;
 *   2. a measurementId is configured (VITE_FIREBASE_MEASUREMENT_ID); and
 *   3. the runtime supports it (browser with cookies/IndexedDB — isSupported()).
 * Every export is a no-op otherwise, so call sites never need to guard.
 *
 * Usage (customer entry only):
 *   import { enableAnalytics } from "@/shared/integrations/firebase/analytics";
 *   enableAnalytics();
 * Elsewhere:
 *   import { trackEvent, setAnalyticsUser } from "@/shared/integrations/firebase/analytics";
 *   trackEvent("login", { method: "password" });
 */
import {
  getAnalytics,
  isSupported,
  logEvent,
  setUserId,
  setUserProperties,
  type Analytics,
} from "@firebase/analytics";
import { app } from "./client";

const measurementId = import.meta.env.VITE_FIREBASE_MEASUREMENT_ID;

let enabled = false;
let analytics: Analytics | null = null;
let initPromise: Promise<Analytics | null> | null = null;

/**
 * Opt this app into analytics. Call once from the customer app entry. The
 * internal QA app deliberately does NOT call it, so it never reports.
 */
export function enableAnalytics(): void {
  enabled = true;
}

async function ensureAnalytics(): Promise<Analytics | null> {
  if (analytics) return analytics;
  if (!enabled || typeof window === "undefined" || !app || !measurementId) return null;
  if (!initPromise) {
    initPromise = isSupported()
      .then((supported) => (supported && app ? getAnalytics(app) : null))
      .then((instance) => (analytics = instance))
      .catch(() => null);
  }
  return initPromise;
}

/** Log a GA4 event. No-op when analytics is disabled/unsupported. */
export function trackEvent(
  name: string,
  params?: Record<string, unknown>,
): void {
  void ensureAnalytics().then((instance) => {
    if (instance) logEvent(instance, name, params);
  });
}

/** Associate subsequent events with a user id (or clear it with null). */
export function setAnalyticsUser(userId: string | null): void {
  void ensureAnalytics().then((instance) => {
    if (instance) setUserId(instance, userId);
  });
}

/** Set GA4 user properties for segmentation (e.g. organization_id). No-op when disabled. */
export function setAnalyticsUserProperties(props: Record<string, string>): void {
  void ensureAnalytics().then((instance) => {
    if (instance) setUserProperties(instance, props);
  });
}
